/**
 * TimelineChart Component
 * Main orchestrator for SVG-based timeline chart with mouse tracking and tooltips
 * Refactored into modular components for maintainability (all files under 500 lines)
 * Enhanced with error handling and performance optimizations
 */

import React, { useRef, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import type { TruckTimelineData, TimelineViewport } from './types';
import { generateTimeTicks, assignLanes } from './utils';
import { useMouseTracking } from './hooks/useMouseTracking';
import { useKeyboardTracking } from './hooks/useKeyboardTracking';
import { useTooltip } from './hooks/useTooltip';
import { useChartDimensions } from './hooks/useChartDimensions';
import { GridLines } from './components/GridLines';
import { TimelineRow } from './components/TimelineRow';
import { TrackingLine } from './components/TrackingLine';
import { TruckTooltip } from './components/TruckTooltip';
import { TimelineErrorBoundary } from './components/TimelineErrorBoundary';
import { BASE_ROW_HEIGHT, LANE_HEIGHT, LANE_SPACING } from './constants';
import { validateViewport, validateTruckData } from './utils/validation';

interface TimelineChartProps {
  data: TruckTimelineData[];
  viewport: TimelineViewport;
  height?: number;
  onZoomIn?: (date: Date) => void;
  onZoomOut?: (date: Date) => void;
  isMobile?: boolean;
  isSmallMobile?: boolean;
}

export const TimelineChart: React.FC<TimelineChartProps> = ({
  data,
  viewport,
  height: propHeight,
  onZoomIn,
  onZoomOut,
  isMobile = false,
  isSmallMobile = false,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  // Custom hooks for state management
  const { chartHeight, chartWidth, innerWidth, innerHeight, padding, deviceType } = useChartDimensions({
    data,
    propHeight,
    isMobile,
    isSmallMobile,
  });

  const { mousePosition, handleMouseMove, handleMouseLeave: handleMouseLeaveTracking } =
    useMouseTracking({
      viewport,
      innerWidth,
      svgRef,
      padding,
    });

  const isCtrlPressed = useKeyboardTracking();

  const { tooltip, handleBarHover, handleBarLeave } = useTooltip({ svgRef });

  // Handle mouse leave to clear both tracking and tooltip
  const handleMouseLeave = useCallback(() => {
    handleMouseLeaveTracking();
    handleBarLeave();
  }, [handleMouseLeaveTracking, handleBarLeave]);

  // Handle click on red line for zoom
  const handleRedLineClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!mousePosition.date) return;

      if (e.ctrlKey || e.metaKey) {
        // Ctrl+Click (or Cmd+Click on Mac) = Zoom Out
        if (onZoomOut) {
          onZoomOut(mousePosition.date);
        }
      } else {
        // Regular Click = Zoom In
        if (onZoomIn) {
          onZoomIn(mousePosition.date);
        }
      }
    },
    [mousePosition.date, onZoomIn, onZoomOut]
  );

  // Generate time ticks
  const ticks = useMemo(() => generateTimeTicks(viewport), [viewport]);

  // Calculate row positions
  const rowPositions = useMemo(() => {
    let currentY = padding.top;
    const baseRowHeight = BASE_ROW_HEIGHT[deviceType as keyof typeof BASE_ROW_HEIGHT];
    const laneHeight = LANE_HEIGHT[deviceType as keyof typeof LANE_HEIGHT];

    return data.map((truckData) => {
      const periodsWithLanes = assignLanes(truckData.periods);
      const maxLane = periodsWithLanes.reduce((max, p) => Math.max(max, p.lane), 0);
      const lanesNeeded = maxLane + 1;
      const rowHeight = Math.max(baseRowHeight, lanesNeeded * (laneHeight + LANE_SPACING));
      const position = currentY;
      currentY += rowHeight;
      return position;
    });
  }, [data, padding.top, deviceType]);

  // Validation: Check viewport data
  if (!validateViewport(viewport)) {
    console.error('Invalid viewport data provided to TimelineChart');
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
          color: '#dc2626',
          fontSize: '0.875rem',
          backgroundColor: '#fee2e2',
          borderRadius: 1,
          padding: 2,
        }}
      >
        Invalid viewport configuration
      </Box>
    );
  }

  // Validation: Check truck data
  if (!validateTruckData(data)) {
    console.error('Invalid truck data provided to TimelineChart');
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
          color: '#dc2626',
          fontSize: '0.875rem',
          backgroundColor: '#fee2e2',
          borderRadius: 1,
          padding: 2,
        }}
      >
        Invalid truck data configuration
      </Box>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
          color: '#6b7280',
          fontSize: '0.875rem',
        }}
      >
        No vehicles to display
      </Box>
    );
  }

  return (
    <TimelineErrorBoundary>
      <Box sx={{ position: 'relative', width: '100%', overflowX: 'auto' }}>
      <svg
        ref={svgRef}
        width={chartWidth}
        height={chartHeight}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ display: 'block' }}
      >
        {/* Define clip path for chart area */}
        <defs>
          <clipPath id="chart-clip">
            <rect
              x={padding.left}
              y={padding.top}
              width={innerWidth}
              height={innerHeight}
            />
          </clipPath>
        </defs>

        {/* Chart background */}
        <rect
          x={padding.left}
          y={padding.top}
          width={innerWidth}
          height={innerHeight}
          fill="#ffffff"
          stroke="#e5e7eb"
          strokeWidth={1}
        />

        {/* X-axis grid lines and ticks */}
        <GridLines
          ticks={ticks}
          viewport={viewport}
          chartHeight={chartHeight}
          innerWidth={innerWidth}
          padding={padding}
        />

        {/* Y-axis labels and data bars */}
        <g>
          {data.map((truckData, index) => (
            <TimelineRow
              key={truckData.truckId}
              truckData={truckData}
              currentY={rowPositions[index] ?? padding.top}
              viewport={viewport}
              mousePosition={mousePosition}
              innerWidth={innerWidth}
              chartWidth={chartWidth}
              isLastRow={index === data.length - 1}
              onBarHover={handleBarHover}
              onBarLeave={handleBarLeave}
              padding={padding}
              deviceType={deviceType as 'desktop' | 'tablet' | 'mobile'}
            />
          ))}
        </g>

        {/* Mouse tracking line (vertical red line) */}
        <TrackingLine
          mousePosition={mousePosition}
          isCtrlPressed={isCtrlPressed}
          chartHeight={chartHeight}
          onRedLineClick={handleRedLineClick}
          padding={padding}
        />

        {/* X-axis line */}
        <line
          x1={padding.left}
          y1={chartHeight - padding.bottom}
          x2={chartWidth - padding.right}
          y2={chartHeight - padding.bottom}
          stroke="#9ca3af"
          strokeWidth={2}
        />
      </svg>

        {/* Tooltip */}
        <TruckTooltip tooltip={tooltip} />
      </Box>
    </TimelineErrorBoundary>
  );
};

export default TimelineChart;
